import { useState, useEffect, useCallback } from 'react';
import izenuAPI from '../../services/izenuAPI';

const TYPE_LABELS = {
    banner: 'Carrossel (Home)',
    home: 'Banner Home',
    player: 'Banner Player',
    artwork: 'Banner Artwork',
    audio: 'Áudio',
};

const TYPE_COLORS = {
    banner: '#3b82f6',
    home: '#10b981',
    player: '#8b5cf6',
    artwork: '#f59e0b',
    audio: '#ec4899',
};

export default function AdsStats() {
    const [perAd, setPerAd] = useState([]);
    const [totals, setTotals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterType, setFilterType] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    const fetchStats = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterType) params.ad_type = filterType;
            if (from) params.from = from;
            if (to) params.to = to;
            const { data } = await izenuAPI.get('/admin/ads/stats.php', { params });
            if (data.response) {
                setPerAd(data.per_ad || []);
                setTotals(data.totals || []);
            }
        } catch (e) {
            console.log('fetch stats error', e);
        }
        setLoading(false);
    }, [filterType, from, to]);

    useEffect(() => { fetchStats(); }, [fetchStats]);

    const summary = {};
    totals.forEach(t => {
        if (!summary[t.ad_type]) summary[t.ad_type] = { impression: 0, click: 0, complete: 0 };
        summary[t.ad_type][t.event_type] = parseInt(t.total, 10) || 0;
    });

    return (
        <div style={s.container}>
            <div style={s.header}>
                <div>
                    <h1 style={s.title}>Estatísticas de Publicidade</h1>
                    <p style={s.subtitle}>Impressões, cliques e CTR por tipo de anúncio e por criativo.</p>
                </div>
            </div>

            {/* Filters */}
            <div style={s.filtersCard}>
                <div>
                    <label style={s.fieldLabel}>Tipo</label>
                    <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={s.input}>
                        <option value="">Todos</option>
                        {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </div>
                <div>
                    <label style={s.fieldLabel}>De</label>
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={s.input} />
                </div>
                <div>
                    <label style={s.fieldLabel}>Até</label>
                    <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={s.input} />
                </div>
                <button onClick={() => { setFilterType(''); setFrom(''); setTo(''); }} style={s.chipBtn}>Limpar</button>
            </div>

            {/* Summary cards */}
            <div style={s.statsGrid}>
                {Object.entries(TYPE_LABELS).map(([type, label]) => {
                    const sm = summary[type] || {};
                    const imp = sm.impression || 0;
                    const clk = sm.click || 0;
                    const ctr = imp > 0 ? ((clk / imp) * 100).toFixed(2) : '0.00';
                    const color = TYPE_COLORS[type];
                    return (
                        <div key={type} style={{ ...s.statCard, borderTop: `3px solid ${color}` }}>
                            <div style={s.statLabel}>{label}</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                <div>
                                    <div style={s.statBigNum}>{imp.toLocaleString('pt-PT')}</div>
                                    <div style={s.statSmall}>Views</div>
                                </div>
                                <div>
                                    <div style={s.statBigNum}>{clk.toLocaleString('pt-PT')}</div>
                                    <div style={s.statSmall}>Clicks</div>
                                </div>
                                <div>
                                    <div style={{ ...s.statBigNum, color }}>{ctr}%</div>
                                    <div style={s.statSmall}>CTR</div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Per-ad table */}
            <div style={s.card}>
                <h3 style={s.cardTitle}>Por anúncio</h3>
                <p style={s.cardDesc}>Lista detalhada de cada criativo com respectivas métricas no período.</p>
                {loading ? (
                    <p style={s.empty}>A carregar...</p>
                ) : perAd.length === 0 ? (
                    <p style={s.empty}>Sem dados para o filtro seleccionado.</p>
                ) : (
                    <table style={s.table}>
                        <thead>
                            <tr>
                                <th style={s.th}>Tipo</th>
                                <th style={s.th}>ID</th>
                                <th style={s.th}>Título</th>
                                <th style={{ ...s.th, textAlign: 'right' }}>Views</th>
                                <th style={{ ...s.th, textAlign: 'right' }}>Únicos/dia</th>
                                <th style={{ ...s.th, textAlign: 'right' }}>Clicks</th>
                                <th style={{ ...s.th, textAlign: 'right' }}>CTR</th>
                                <th style={{ ...s.th, textAlign: 'right' }}>Completes</th>
                                <th style={s.th}>Último evento</th>
                            </tr>
                        </thead>
                        <tbody>
                            {perAd.map((row) => (
                                <tr key={`${row.ad_type}-${row.ad_id}`} style={s.tr}>
                                    <td style={s.td}>
                                        <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, background: (TYPE_COLORS[row.ad_type] || '#888') + '18', color: TYPE_COLORS[row.ad_type] || '#374151', fontWeight: 600 }}>
                                            {TYPE_LABELS[row.ad_type] || row.ad_type}
                                        </span>
                                    </td>
                                    <td style={{ ...s.td, color: '#9ca3af' }}>#{row.ad_id}</td>
                                    <td style={s.td}>{row.title || '—'}</td>
                                    <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{parseInt(row.impressions, 10).toLocaleString('pt-PT')}</td>
                                    <td style={{ ...s.td, textAlign: 'right', color: '#9ca3af' }}>{parseInt(row.unique_impressions, 10).toLocaleString('pt-PT')}</td>
                                    <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{parseInt(row.clicks, 10).toLocaleString('pt-PT')}</td>
                                    <td style={{ ...s.td, textAlign: 'right', color: row.ctr > 0 ? '#16a34a' : '#9ca3af', fontWeight: 600 }}>{row.ctr}%</td>
                                    <td style={{ ...s.td, textAlign: 'right', color: '#9ca3af' }}>{parseInt(row.completes, 10).toLocaleString('pt-PT')}</td>
                                    <td style={{ ...s.td, color: '#9ca3af', fontSize: 12 }}>{row.last_event_at ? new Date(row.last_event_at).toLocaleString('pt-PT') : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

const s = {
    container: { padding: '0 20px 40px 20px', maxWidth: 1400 },
    header: { marginBottom: 20 },
    title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
    subtitle: { margin: '4px 0 0', fontSize: 13, color: '#999', fontWeight: 400 },
    filtersCard: { display: 'flex', gap: 14, alignItems: 'flex-end', padding: 16, backgroundColor: '#fff', borderRadius: 10, boxShadow: '0 1px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', marginBottom: 20, flexWrap: 'wrap' },
    fieldLabel: { display: 'block', fontSize: 11, color: '#888', fontWeight: 500, marginBottom: 4 },
    input: { padding: '8px 12px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', color: '#1f2937', outline: 'none' },
    chipBtn: { padding: '8px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 24 },
    statCard: { backgroundColor: '#fff', padding: 16, borderRadius: 10, boxShadow: '0 1px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' },
    statLabel: { color: '#888', fontSize: 11, textTransform: 'uppercase', marginBottom: 10, fontWeight: 600 },
    statBigNum: { fontSize: 20, fontWeight: 700, color: '#1f2937' },
    statSmall: { color: '#9ca3af', fontSize: 11, marginTop: 2 },
    card: { padding: 24, backgroundColor: '#fff', borderRadius: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' },
    cardTitle: { fontSize: 15, fontWeight: 600, color: '#333', margin: '0 0 4px' },
    cardDesc: { margin: '0 0 14px', fontSize: 12, color: '#aaa' },
    empty: { color: '#ccc', textAlign: 'center', padding: 40, fontSize: 14 },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e5e7eb' },
    tr: { borderBottom: '1px solid #f5f5f5' },
    td: { padding: '10px 8px', fontSize: 13, color: '#374151' },
};
